import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Axios from 'axios';
import parse from 'html-react-parser';
import { Helmet } from 'react-helmet-async';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

import {
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  TelegramShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  RedditShareButton,
} from 'react-share';

import { Loading } from './Loading';
import {
  PageContainer,
  PageHero,
  Section,
  Protected,
} from '../theme/primitives';
import { ShareGlyph, ShareBubble } from './ShareIcons';

const key = 'dqweqwe123y97qhduiasd$@^!*#&bjasdja&^&@!#';

const I = {
  Calendar: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  ),
  Link: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
  Check: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M5 12l5 5L20 7" />
    </svg>
  ),
};

export const SingleAwardAndNew = () => {
  const { id } = useParams();
  const url = window.location.href;

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    Axios.post('/new_apis/get-single-article', { id }, { headers: { key } }).then((res) => {
      setData(res.data.article_data || null);
      setMetadata(res.data.seo_data?.[0] || null);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  const openLightbox = (startIndex) => {
    const images = (data?.article_images || '').split(',').filter(Boolean);
    const rotated = startIndex <= 0 ? images : [...images.slice(startIndex), ...images.slice(0, startIndex)];
    setLightboxImages(rotated);
    setLightboxOpen(true);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) return <Loading />;
  if (!data) return null;

  const images = (data.article_images || '').split(',').filter(Boolean);
  const hero = images[0];
  const rest = images.slice(1, 4);
  const extra = images.length - 4;

  return (
    <Protected>
      {metadata && (
        <Helmet>
          <title>{metadata.seo_title}</title>
          <meta name="description" content={metadata.seo_desc} />
          <meta name="keywords" content={metadata.seo_keywords} />
        </Helmet>
      )}

      <PageContainer maxWidth="lg">
        <PageHero compact kicker="Honors & Highlights" title="Awards & News" />

        <Section>
          <article className="
            relative overflow-hidden rounded-2xl
            border border-border-subtle bg-surface-paper
            p-4 sm:p-6 md:p-8
          ">
            <span aria-hidden className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-amber/50 to-transparent" />

            {/* Date pill */}
            <div className="inline-flex items-center gap-1.5 rounded-full border border-amber/24 bg-amber/10 px-2.5 py-1">
              <I.Calendar className="h-3 w-3 text-amber" />
              <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-amber">{data.article_published_date}</span>
            </div>

            {/* Title */}
            <h1 className="mt-3 text-[22px] sm:text-[28px] md:text-[32px] font-bold leading-[1.18] tracking-[-0.022em] text-fg-primary">
              {data.article_title}
            </h1>

            {/* Amber accent line */}
            <div aria-hidden className="mt-3 h-[2px] w-12 rounded-full bg-gradient-to-r from-amber to-amber/0" />

            {/* Hero + thumbs */}
            {hero && (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => openLightbox(0)}
                  className="block w-full overflow-hidden rounded-xl border border-border-subtle bg-surface-overlay group"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={`/articles/${hero}`}
                      alt={data.article_title}
                      className="h-full w-full object-cover cursor-zoom-in transition-transform duration-700 ease-out-soft group-hover:scale-[1.03]"
                    />
                  </div>
                </button>
                {rest.length > 0 && (
                  <div className="mt-2 hidden sm:grid grid-cols-4 gap-2">
                    {rest.map((img, idx) => {
                      const isLast = idx === 2 && extra > 0;
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => openLightbox(idx + 1)}
                          className="relative aspect-square overflow-hidden rounded-md bg-surface-overlay border border-border-subtle hover:border-amber/40 transition-colors duration-200 cursor-zoom-in"
                        >
                          <img src={`/articles/${img}`} alt="" className={`h-full w-full object-cover ${isLast ? 'opacity-45' : ''}`} />
                          {isLast && (
                            <span className="absolute inset-0 flex items-center justify-center bg-black/40 text-amber font-bold text-[18px]">
                              +{extra}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Share row */}
            <div className="mt-6 pt-4 border-t border-border-subtle flex flex-wrap items-center gap-2">
              <span className="mr-2 text-[10px] font-bold uppercase tracking-[0.18em] text-amber">Share</span>
              <FacebookShareButton url={url}><ShareBubble label="Facebook" size="sm"><ShareGlyph.Facebook /></ShareBubble></FacebookShareButton>
              <LinkedinShareButton url={url}><ShareBubble label="LinkedIn" size="sm"><ShareGlyph.LinkedIn /></ShareBubble></LinkedinShareButton>
              <TwitterShareButton url={url}><ShareBubble label="X" size="sm"><ShareGlyph.X /></ShareBubble></TwitterShareButton>
              <RedditShareButton url={url}><ShareBubble label="Reddit" size="sm"><ShareGlyph.Reddit /></ShareBubble></RedditShareButton>
              <WhatsappShareButton url={url}><ShareBubble label="WhatsApp" size="sm"><ShareGlyph.WhatsApp /></ShareBubble></WhatsappShareButton>
              <TelegramShareButton url={url}><ShareBubble label="Telegram" size="sm"><ShareGlyph.Telegram /></ShareBubble></TelegramShareButton>
              <EmailShareButton url={url}><ShareBubble label="Email" size="sm"><ShareGlyph.Email /></ShareBubble></EmailShareButton>

              <button
                type="button"
                onClick={handleCopy}
                aria-label={copied ? 'Link copied' : 'Copy link'}
                title={copied ? 'Link copied!' : 'Click to copy the URL'}
                className={`
                  inline-flex h-9 w-9 items-center justify-center rounded-full
                  border bg-surface-elevated cursor-pointer
                  transition-[background-color,border-color,color,transform,box-shadow] duration-200 ease-out-soft
                  hover:bg-amber hover:border-amber hover:text-ink hover:-translate-y-0.5
                  hover:shadow-[0_8px_18px_-6px_rgba(251,191,36,0.55)]
                  ${copied ? 'border-amber text-amber' : 'border-border-subtle text-fg-secondary'}
                `}
              >
                {copied ? <I.Check className="h-4 w-4" /> : <I.Link className="h-4 w-4" />}
              </button>
            </div>

            {/* Body */}
            <div
              className="
                mt-5 rounded-xl border p-4 sm:p-5
                text-[14px] leading-[1.7]
                [&_p]:mb-3
                [&_a]:text-amber-deep [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-amber
                [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-md [&_img]:my-3
                [&_h1]:mt-4 [&_h1]:mb-2 [&_h1]:text-[19px] [&_h1]:font-bold
                [&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:text-[17px] [&_h2]:font-bold
                [&_h3]:mt-3 [&_h3]:mb-2 [&_h3]:text-[15px] [&_h3]:font-bold
                [&_strong]:font-bold
                [&_b]:font-bold
                [&_ul]:pl-5 [&_ul]:list-disc [&_ul]:my-2
                [&_ol]:pl-5 [&_ol]:list-decimal [&_ol]:my-2
                [&_li]:my-0.5
              "
              style={{
                backgroundColor: '#FFFFFF',
                borderColor: 'rgba(10,10,11,0.08)',
                color: '#1f1f23',
                boxShadow: '0 4px 16px -8px rgba(0,0,0,0.4)',
              }}
            >
              {parse(data.article_desc || '')}
            </div>
          </article>
        </Section>

        {lightboxOpen && (
          <Lightbox
            open={lightboxOpen}
            close={() => setLightboxOpen(false)}
            slides={lightboxImages.map((src) => ({ src: `/articles/${src}` }))}
          />
        )}
      </PageContainer>
    </Protected>
  );
};
